import { useEffect, useState } from 'react';
import {
  Box, Button, InputAdornment, Stack, TextField,
} from '@mui/material';

import type { Poi } from '../api/types';
import { PlaceAutocomplete } from './PlaceAutocomplete';
import { SitesModeSwitch, type SitesMode } from './SitesModeSwitch';
import { BreakRecommendations, type RecommendationState } from './StopRecommendations';

/*
 * The kinds of place each break looks for.
 *
 * Mirrors LUNCH_CATEGORIES and COFFEE_CATEGORIES on the server, so the picker
 * offers the same places the planner would have chosen from.
 */
export const BREAK_CATEGORIES = {
  lunch: ['restaurant', 'fast_food'],
  coffee: ['cafe', 'ice_cream'],
};

/**
 * Everything about a break except whether it is happening.
 *
 * Split out from the one-day form's row so a multi-day operation's per-day
 * cards ask the same questions in the same words. They had drifted: a day
 * card pinned every break to an hour with no way to leave the timing to the
 * planner, wrote its length through `Number()` on each keystroke (so 45 to 30
 * passed through 3 and landed on 50), offered the place picker and the
 * suggestions at once with no sense that they were one decision, and kept its
 * own copy of the category lists.
 *
 * The question of whether the break happens at all differs between the two —
 * a switch on one day, a three-way "as the trip / yes / no" on a day of an
 * operation — so that stays with each caller and only the rest is shared.
 */
export function BreakFields({
  label, at, onAtChange, minutes, onMinutesChange,
  cityId, categoryKeys, place, onPlaceChange, placeholder,
  role, recommendations, onShowDetails, canSuggest = true,
  notReadyLabel,
  mode, onModeChange, pinned, onPinnedChange, canShowOnMap = false,
}: {
  /** Names the break in the controls' accessible labels. */
  label: string;
  /** Whether this break is tied to an hour, or left to the planner. */
  pinned: boolean;
  onPinnedChange: (next: boolean) => void;
  /** Whether the planner finds this stop, or the traveller names it. */
  mode: SitesMode;
  onModeChange: (next: SitesMode) => void;
  /** Which break this is, so it can pull its own half of the suggestions. */
  role: 'lunch' | 'coffee';
  /** Shared with the rest of the form: one request answers all three lists. */
  recommendations: RecommendationState;
  /**
   * Whether there is a corridor to rank against. False disables the suggest
   * button: asking without one quietly returns nothing, which reads as a
   * broken button rather than a missing journey.
   */
  canSuggest?: boolean;
  /** What is missing, said on that button. Differs by where this sits. */
  notReadyLabel?: string;
  /** Whether the page this sits on draws the suggestion markers. */
  canShowOnMap?: boolean;
  onShowDetails: (poiId: string) => void;
  at: string;
  onAtChange: (value: string) => void;
  minutes: number;
  onMinutesChange: (minutes: number) => void;
  cityId: string;
  /** Narrows the place search to the kinds of place this break is for. */
  categoryKeys: string[];
  /** A place the traveller chose, or null to let the planner decide. */
  place: Poi | null;
  onPlaceChange: (poi: Poi | null) => void;
  placeholder: string;
}) {
  /*
   * What is typed, held as typed, and only interpreted when the field is left.
   *
   * The length was written straight through a clamp on every keystroke, and
   * the field is controlled — so changing 45 to 30 began by typing "3", which
   * was read as 3 minutes, raised to the 5-minute floor, and written back.
   * The next keystroke appended to "5" and the answer became 50. Thirty was
   * not reachable at all.
   *
   * The same arrangement the avoidance distances use, and for the same
   * reason: the bounds still apply, on commit, where they cannot fight the
   * typist.
   */
  const [minutesText, setMinutesText] = useState(String(minutes));
  useEffect(() => { setMinutesText(String(minutes)); }, [minutes]);

  const commitMinutes = () => {
    const raw = minutesText.trim();
    if (raw === '') {
      // Emptied and left: put back what is actually in force rather than
      // committing a zero nobody typed.
      setMinutesText(String(minutes));
      return;
    }
    const next = Number(raw);
    if (!Number.isFinite(next)) {
      setMinutesText(String(minutes));
      return;
    }
    onMinutesChange(next);
  };

  return (
    <>
      <Stack direction="row" spacing={1.5} sx={{ pt: 0.5, alignItems: 'center' }}>
        {/*
          An hour only when one is wanted.

          Pinning a break is a hard constraint on the optimiser, so the field
          is offered rather than imposed: left alone, the planner fits it
          wherever the round allows, which is what most people mean by "a
          lunch stop".
        */}
        {pinned ? (
          <TextField
            size="small"
            type="time"
            label="Around"
            value={at}
            onChange={(event) => onAtChange(event.target.value)}
            sx={{ flex: 1 }}
          />
        ) : (
          /*
            The state, not an instruction.

            This read "Any time — set one", which is two things at once: what
            the break is doing now and what pressing it would do. Said plainly
            it is "Auto time", and the way to change it is the link under the
            row — where the way BACK already was, so the two now sit in the
            same place and read as one pair.
          */
          <Button
            size="small"
            variant="outlined"
            onClick={() => onPinnedChange(true)}
            sx={{ flex: 1, textTransform: 'none', height: 40, whiteSpace: 'nowrap' }}
          >
            Auto time
          </Button>
        )}
        <TextField
          size="small"
          type="number"
          label="For"
          value={minutesText}
          onChange={(event) => setMinutesText(event.target.value)}
          onBlur={commitMinutes}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commitMinutes();
            }
          }}
          slotProps={{
            htmlInput: { min: 5, max: 240, step: 5, 'aria-label': `${label} length in minutes` },
            input: { endAdornment: <InputAdornment position="end">min</InputAdornment> },
          }}
          sx={{ width: 108 }}
        />
      </Stack>

      {/*
        The way between the two, in one place.

        Under the row rather than inside the field. As a clear button in the
        input it had to share the width with the time, the AM/PM and the
        browser's own clock, and at sidebar width the hour was cut off. The
        hour is a mode rather than a value, so it belongs beside the mode it
        changes — and setting one used to be one-way, so a break pinned by
        accident, or to an hour the round never reaches, stayed pinned for
        the life of the form.

        The hour itself is kept either way, so going back and forth costs
        nothing: setting one again offers what was there rather than a
        default.
      */}
      <Box sx={{ pt: 0.5 }}>
        <Button
          size="small"
          onClick={() => onPinnedChange(!pinned)}
          sx={{ textTransform: 'none', p: 0, minWidth: 0 }}
        >
          {pinned ? 'Auto time instead' : 'Set my own time'}
        </Button>
      </Box>

      {/*
        Empty means the planner chooses one near where the traveller will be
        when the break is due. Naming a place is the exception, so it reads as
        optional rather than as a field waiting to be filled.
      */}
      <Box sx={{ pt: 1.5 }}>
        <SitesModeSwitch variant="break" mode={mode} onChange={onModeChange} />

        {mode === 'named' && (
          <PlaceAutocomplete
            cityId={cityId}
            label="Which place?"
            placeholder={placeholder}
            categoryKeys={categoryKeys}
            value={place}
            onChange={onPlaceChange}
            {...(place ? {} : { helperText: 'Search, or take a suggestion below' })}
          />
        )}

        {/*
          Suggestions sit under the picker they fill in, not in a list of
          their own. Deciding where to eat is a decision made here.
        */}
        {mode === 'named' && (
          <BreakRecommendations
            state={recommendations}
            ready={canSuggest}
            {...(notReadyLabel ? { notReadyLabel } : {})}
            role={role}
            cityId={cityId}
            chosen={place}
            onChoose={onPlaceChange}
            onShowDetails={onShowDetails}
            canShowOnMap={canShowOnMap}
          />
        )}
      </Box>
    </>
  );
}
